import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#D4A747',
      light: '#E8D5A8',
      dark: '#B8901C',
      contrastText: '#1A1A1A',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#2B2B2B',
      dark: '#0A0A0A',
      contrastText: '#F4E4BC',
    },
    gold: {
      main: '#D4A747',
      light: '#F4E4BC',
      medium: '#C9A961',
      dark: '#B8901C',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      dark: '#1A1A1A',
      accent: '#F9F6F0',
    },
    typography: {
      main: '#2B2B2B',
      secondary: '#5A5A5A',
      light: '#757575',
    },
  },
  typography: {
    fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
    h1: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.3,
      '@media (min-width:768px)': {
        fontSize: '4.5rem',
      },
    },
    h2: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 24px',
          fontWeight: 500,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '1280px !important',
          paddingLeft: '16px',
          paddingRight: '16px',
          overflow: 'visible',
          '@media (min-width: 640px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
          '@media (min-width: 1024px)': {
            paddingLeft: '32px',
            paddingRight: '32px',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '80px !important',
          height: '80px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          overflow: 'visible',
          paddingTop: '3px',
          paddingBottom: '3px',
        },
        h1: {
          paddingTop: '8px',
          paddingBottom: '8px',
        },
        h2: {
          paddingTop: '6px',
          paddingBottom: '6px',
        },
        h3: {
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        h4: {
          paddingTop: '3px',
          paddingBottom: '3px',
        },
        h5: {
          paddingTop: '3px',
          paddingBottom: '3px',
        },
        h6: {
          paddingTop: '3px',
          paddingBottom: '3px',
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          overflow: 'visible',
          '&.gradient-primary': {
            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
          },
          '&.gradient-gold': {
            background: 'linear-gradient(135deg, #F4E4BC 0%, #D4A747 100%)',
          },
          '&.gradient-text': {
            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
});

export default theme;
